import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "@/data/content";
import { EASE, lineMask } from "@/lib/motion";

const HEADLINE = ["Where sound", "becomes", "sanctuary."];

const Hero = () => {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // subtle parallax: image drifts down, content lifts, overlay deepens
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.32, 0.6]);

  const scrollDown = () => {
    const el = document.getElementById("intro");
    if (el && lenis) lenis.scrollTo(el, { offset: -60, duration: 1.4 });
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden grain"
    >
      {/* Parallax background */}
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img
          src={IMAGES.hero}
          alt="A tranquil, light-filled wellness sanctuary"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        className="absolute inset-0 bg-forest"
        style={{ opacity: overlay }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-forest/70 via-transparent to-charcoal/20"
        aria-hidden="true"
      />

      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-24"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 1, ease: EASE }}
          className="kicker text-ivory/80 mb-6 md:mb-8"
        >
          Music Therapy · Energy Healing · Mindful Living
        </motion.p>

        <h1 className="font-serif font-light text-ivory text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight max-w-5xl">
          {HEADLINE.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span
                className="block"
                variants={lineMask}
                initial="hidden"
                animate="visible"
                transition={{ duration: 1.15, ease: EASE, delay: 0.35 + i * 0.14 }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 md:mt-14 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 1, ease: EASE }}
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-ivory text-forest px-9 py-4 uppercase tracking-[0.16em] text-xs transition-colors duration-500 hover:bg-gold hover:text-forest"
            >
              Begin Your Wellness Journey
              <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="text-ivory/75 font-light max-w-md leading-relaxed text-sm md:text-base"
          >
            A refined space where ancient wisdom meets contemporary wellbeing —
            designed to return you, gently, to yourself.
          </motion.p>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.button
        onClick={scrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        aria-label="Scroll to explore"
        data-testid="hero-scroll-cue"
        className="absolute bottom-8 right-6 md:right-10 z-10 hidden md:flex flex-col items-center gap-3 text-ivory/70"
      >
        <span className="kicker text-[0.6rem] [writing-mode:vertical-rl]">Scroll</span>
        <span className="animate-float-slow">
          <ArrowDown size={18} strokeWidth={1.3} />
        </span>
      </motion.button>
    </section>
  );
};

export default Hero;
